import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useContainerPermissions } from '@/hooks/useVisibility';
import type {
  AadGroupSummary,
  ContainerPermissions,
  ContainerRole,
  ProfileSummary,
  SetRestrictionPrincipal,
} from '@/services/api/endpoints';

// État de saisie de la popup « Gérer la visibilité ».
//
// Le serveur ne connaît pas de delta : `setContainerRestriction` efface les
// droits manuels puis réinsère la liste envoyée. Le formulaire doit donc porter
// à tout instant la liste COMPLÈTE des ayants droit, y compris ceux qu'on
// affiche sans pouvoir les modifier (droits legacy posés par OID Entra).

/**
 * Une personne retenue.
 *
 * `email` est la clé nominale — c'est lui qui repart en `principalUpn`. `oid`
 * n'est renseigné que pour un droit legacy relu (`email === null`) : sans lui,
 * ce droit disparaîtrait au premier enregistrement.
 */
export interface SelectedUser {
  email: string | null;
  oid: string | null;
  displayName: string | null;
  fonction: string | null;
  photoUrl: string | null;
  /**
   * Rôle relu côté serveur, réémis tel quel. On ne l'expose pas dans l'UI (la
   * popup ne gère que la visibilité), mais l'omettre ferait retomber un droit
   * `admin`/`reader` existant sur le défaut serveur `contributor`.
   */
  role?: ContainerRole;
  /** false = personne absente de l'annuaire PRISM (départ, prestataire). */
  knownInPrism: boolean;
}

export interface SelectedGroup {
  groupId: string;
  displayName: string | null;
  /** Effectif connu du cache local ; `null` pour un groupe tout juste coché. */
  memberCount: number | null;
  role?: ContainerRole;
}

export interface VisibilityForm {
  restricted: boolean;
  users: SelectedUser[];
  groups: SelectedGroup[];
}

/**
 * Clé de déduplication d'une personne. Les e-mails sont comparés en minuscules :
 * l'annuaire et les droits relus peuvent différer de casse pour la même personne.
 */
export function userKey(u: Pick<SelectedUser, 'email' | 'oid'>): string {
  return u.email ? `u:${u.email.toLowerCase()}` : `o:${u.oid ?? ''}`;
}

/** Libellé d'un groupe. Jamais le `groupId` : c'est un identifiant technique. */
export function groupLabel(g: { displayName: string | null }): string {
  return g.displayName?.trim() || 'Groupe sans nom';
}

/** Libellé d'une personne : son nom, sinon son e-mail (jamais son OID). */
export function userLabel(u: SelectedUser): string {
  return u.displayName?.trim() || u.email || 'Compte sans e-mail connu';
}

/**
 * Traduit le formulaire en liste d'ayants droit à écrire.
 *
 * « Visibilité publique » ⇒ liste vide : le serveur applique déjà la règle
 * inverse (restreint sans destinataire = public), on garde donc l'invariant
 * « public ⇔ aucun droit manuel » plutôt que de laisser traîner des droits
 * invisibles.
 */
export function buildPermissions(form: VisibilityForm): SetRestrictionPrincipal[] {
  if (!form.restricted) return [];
  const out: SetRestrictionPrincipal[] = [];
  for (const u of form.users) {
    if (u.email) {
      out.push({ principalType: 'user', principalUpn: u.email, ...(u.role ? { role: u.role } : {}) });
    } else if (u.oid) {
      // Droit legacy : seul cas où `principalId` est légitime pour un utilisateur.
      out.push({ principalType: 'user', principalId: u.oid, ...(u.role ? { role: u.role } : {}) });
    }
  }
  for (const g of form.groups) {
    out.push({ principalType: 'aad_group', principalId: g.groupId, ...(g.role ? { role: g.role } : {}) });
  }
  return out;
}

function fromPermissions(p: ContainerPermissions): VisibilityForm {
  const users: SelectedUser[] = [];
  const groups: SelectedGroup[] = [];
  for (const pr of p.principals) {
    if (pr.type === 'user') {
      // Ni e-mail ni OID : rien de réécrivable, on ne l'embarque pas (le
      // renvoyer produirait un droit vide, l'afficher n'aiderait personne).
      if (!pr.email && !pr.oid) continue;
      users.push({
        email: pr.email,
        oid: pr.oid,
        displayName: pr.displayName,
        fonction: pr.fonction,
        photoUrl: pr.photoUrl,
        role: pr.role,
        knownInPrism: pr.knownInPrism,
      });
    } else {
      groups.push({
        groupId: pr.groupId,
        displayName: pr.displayName,
        memberCount: pr.memberCount,
        role: pr.role,
      });
    }
  }
  return { restricted: p.isRestricted, users, groups };
}

/** Empreinte stable du formulaire, pour savoir s'il y a quelque chose à enregistrer. */
function signature(f: VisibilityForm): string {
  const keys = [...f.users.map(userKey), ...f.groups.map((g) => `g:${g.groupId}`)].sort();
  return `${f.restricted ? 'restricted' : 'public'}|${keys.join(',')}`;
}

/**
 * Formulaire de visibilité d'un espace : relecture de l'état serveur à
 * l'ouverture, puis pilotage local de la sélection.
 *
 * À N'APPELER QUE DEPUIS UN COMPOSANT MONTÉ LE TEMPS DE LA POPUP (cf. le corps
 * de ContainerVisibilityDialog). C'est ce qui remplace l'ancienne remise à zéro
 * à la fermeture : le brouillon meurt avec le composant, donc la popup rouverte
 * repart forcément de l'état serveur — quelqu'un d'autre ayant pu changer les
 * droits entre-temps, la gestion étant ouverte à tous.
 */
export function useVisibilityForm(containerId: string) {
  const query = useContainerPermissions(containerId);
  const [form, setForm] = useState<VisibilityForm | null>(null);
  const [initial, setInitial] = useState<VisibilityForm | null>(null);
  // Version (`dataUpdatedAt`) déjà appliquée au formulaire, et « l'utilisateur a
  // touché à quelque chose » : deux gardes pour la ré-hydratation ci-dessous.
  const hydratedAt = useRef(0);
  const touched = useRef(false);

  // Hydratation depuis la lecture serveur. On accepte une relecture plus fraîche
  // (le cache React Query peut répondre avant le refetch déclenché à
  // l'ouverture) mais jamais par-dessus une saisie en cours.
  useEffect(() => {
    if (!query.data) return;
    if (hydratedAt.current === query.dataUpdatedAt) return;
    if (touched.current) return;
    hydratedAt.current = query.dataUpdatedAt;
    const next = fromPermissions(query.data);
    setForm(next);
    setInitial(next);
  }, [query.data, query.dataUpdatedAt]);

  const update = useCallback((fn: (f: VisibilityForm) => VisibilityForm) => {
    touched.current = true;
    setForm((cur) => (cur ? fn(cur) : cur));
  }, []);

  const setRestricted = useCallback(
    (restricted: boolean) => update((f) => ({ ...f, restricted })),
    [update],
  );

  const toggleUser = useCallback(
    (p: ProfileSummary) =>
      update((f) => {
        const key = userKey({ email: p.email, oid: null });
        const already = f.users.some((u) => userKey(u) === key);
        return {
          ...f,
          users: already
            ? f.users.filter((u) => userKey(u) !== key)
            : [
                ...f.users,
                {
                  email: p.email,
                  oid: null,
                  displayName: p.displayName,
                  fonction: p.fonction,
                  photoUrl: p.photoUrl,
                  knownInPrism: true,
                },
              ],
        };
      }),
    [update],
  );

  const toggleGroup = useCallback(
    (g: AadGroupSummary) =>
      update((f) => {
        const already = f.groups.some((x) => x.groupId === g.groupId);
        return {
          ...f,
          groups: already
            ? f.groups.filter((x) => x.groupId !== g.groupId)
            : [...f.groups, { groupId: g.groupId, displayName: g.displayName, memberCount: null }],
        };
      }),
    [update],
  );

  const removeUser = useCallback(
    (key: string) => update((f) => ({ ...f, users: f.users.filter((u) => userKey(u) !== key) })),
    [update],
  );

  const removeGroup = useCallback(
    (groupId: string) =>
      update((f) => ({ ...f, groups: f.groups.filter((g) => g.groupId !== groupId) })),
    [update],
  );

  const selectedUserKeys = useMemo(
    () => new Set((form?.users ?? []).map(userKey)),
    [form?.users],
  );
  const selectedGroupIds = useMemo(
    () => new Set((form?.groups ?? []).map((g) => g.groupId)),
    [form?.groups],
  );

  const isDirty = useMemo(
    () => (form && initial ? signature(form) !== signature(initial) : false),
    [form, initial],
  );

  return {
    query,
    form,
    /** Droits tels qu'ils étaient à l'ouverture — sert à prévenir avant de les effacer. */
    initial,
    isDirty,
    selectedUserKeys,
    selectedGroupIds,
    setRestricted,
    toggleUser,
    toggleGroup,
    removeUser,
    removeGroup,
  };
}
